/*
  DeleteWithReasonDialog — reusable confirmation dialog that requires
  a typed reason before firing the caller's onConfirm. Used by
  the SOAP note, Assessment and Attachment delete buttons — everything
  that soft-deletes clinical records for an audit trail.

  Props:
    open, onOpenChange       — modal state
    title, description       — copy shown in the header (short strings)
    itemLabel                — bolded name of the item being deleted
    confirmLabel             — CTA button label ("Delete note" etc.)
    onConfirm(reason)        — async fn; resolves on success, rejects on error.
                                Dialog closes itself on resolve.
    reasonPresets            — optional list of quick-pick reasons
    testidPrefix             — data-testid namespace for the reason input
                                and confirm button ("soap-delete" etc.)
*/
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_PRESETS = [
  "Wrong client",
  "Duplicate",
  "Created by mistake",
  "Contains PHI for another patient",
];

export default function DeleteWithReasonDialog({
  open,
  onOpenChange,
  title = "Delete this record?",
  description = "This will hide the record from the workspace. An admin can restore it later from Deleted Items.",
  itemLabel = "",
  confirmLabel = "Delete",
  onConfirm,
  reasonPresets = DEFAULT_PRESETS,
  testidPrefix = "delete-with-reason",
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setBusy(false);
    }
  }, [open]);

  const canSubmit = reason.trim().length >= 3 && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } catch (e) {
      toast.error("Delete failed", {
        description: e?.response?.data?.detail || e.message || "Try again",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        {itemLabel && (
          <div className="rounded-md bg-red-50 border border-red-200 p-2 text-xs text-red-900">
            Deleting: <span className="font-medium">{itemLabel}</span>
          </div>
        )}

        <div className="space-y-2">
          <label className="text-xs font-medium text-zinc-700 block">
            Reason <span className="text-red-600">*</span>
          </label>
          <div className="flex flex-wrap gap-1">
            {reasonPresets.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setReason(p)}
                className="text-2xs px-2 py-0.5 rounded-full border border-zinc-200 hover:bg-zinc-100 hover:border-zinc-300 transition-colors"
                data-testid={`${testidPrefix}-preset-${p.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {p}
              </button>
            ))}
          </div>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this being deleted? (required, min 3 chars)"
            className="text-sm min-h-[72px]"
            maxLength={500}
            data-testid={`${testidPrefix}-reason-input`}
          />
          <div className="text-2xs text-zinc-400 text-right">
            {reason.trim().length}/500
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={!canSubmit}
            data-testid={`${testidPrefix}-confirm-btn`}
          >
            {busy ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Deleting…</>
            ) : (
              confirmLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
